/**
 * Centralized Status Color Maps (DRY-1)
 *
 * Single source of truth for entity status → badge color mappings.
 * Used by both TMS and WMS grid pages.
 *
 * Colors use Chakra UI v3 BadgeColorPalette values.
 * Keys are UPPERCASE to match badge display convention.
 */

export type StatusColorPalette =
  | 'gray'
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'purple'
  | 'brand';

// ── TMS Entity Status Colors ──

export const ORDER_STATUS_COLORS: Record<string, StatusColorPalette> = {
  DRAFT: 'gray',
  PENDING: 'yellow',
  CONFIRMED: 'blue',
  PROCESSING: 'purple',
  IN_TRANSIT: 'purple',
  DELIVERED: 'green',
  COMPLETED: 'green',
  CANCELLED: 'red',
  ON_HOLD: 'orange',
  RETURNED: 'orange',
} as const;

export const ORDER_PRIORITY_COLORS: Record<string, StatusColorPalette> = {
  LOW: 'green',
  NORMAL: 'blue',
  HIGH: 'orange',
  URGENT: 'red',
  CRITICAL: 'red',
} as const;

export const ORDER_TYPE_COLORS: Record<string, StatusColorPalette> = {
  STANDARD: 'gray',
  EXPRESS: 'blue',
  SAME_DAY: 'purple',
  NEXT_DAY: 'purple',
  SCHEDULED: 'green',
  RECURRING: 'blue',
  BULK: 'orange',
  SAMPLE: 'green',
} as const;

export const SHIPMENT_STATUS_COLORS: Record<string, StatusColorPalette> = {
  PLANNED: 'gray',
  ASSIGNED: 'blue',
  PICKED_UP: 'blue',
  IN_TRANSIT: 'purple',
  OUT_FOR_DELIVERY: 'yellow',
  DELIVERED: 'green',
  FAILED: 'red',
  RETURNED: 'orange',
  CANCELLED: 'red',
  ON_HOLD: 'gray',
} as const;

export const CUSTOMER_STATUS_COLORS: Record<string, StatusColorPalette> = {
  ACTIVE: 'green',
  INACTIVE: 'gray',
  SUSPENDED: 'orange',
  BLOCKED: 'red',
} as const;

export const CUSTOMER_TYPE_COLORS: Record<string, StatusColorPalette> = {
  INDIVIDUAL: 'green',
  CORPORATE: 'blue',
} as const;

export const INVOICE_STATUS_COLORS: Record<string, StatusColorPalette> = {
  DRAFT: 'gray',
  SENT: 'blue',
  PAID: 'green',
  OVERDUE: 'red',
  PARTIAL_PAID: 'yellow',
  CANCELLED: 'gray',
} as const;

export const PAYMENT_STATUS_COLORS: Record<string, StatusColorPalette> = {
  PENDING: 'yellow',
  PROCESSING: 'purple',
  COMPLETED: 'green',
  FAILED: 'red',
  REFUNDED: 'orange',
} as const;

export const EXPENSE_STATUS_COLORS: Record<string, StatusColorPalette> = {
  PENDING: 'yellow',
  APPROVED: 'green',
  REJECTED: 'red',
  PAID: 'blue',
} as const;

export const DRIVER_STATUS_COLORS: Record<string, StatusColorPalette> = {
  ACTIVE: 'green',
  INACTIVE: 'gray',
  ON_LEAVE: 'orange',
  SUSPENDED: 'red',
} as const;

export const POWER_UNIT_STATUS_COLORS: Record<string, StatusColorPalette> = {
  AVAILABLE: 'green',
  ASSIGNED: 'blue',
  MAINTENANCE: 'orange',
  OUT_OF_SERVICE: 'red',
  RETIRED: 'gray',
} as const;

export const TRAILER_STATUS_COLORS: Record<string, StatusColorPalette> = {
  AVAILABLE: 'green',
  ASSIGNED: 'blue',
  LOADING: 'orange',
  LOADED: 'purple',
  IN_TRANSIT: 'blue',
  UNLOADING: 'orange',
  MAINTENANCE: 'yellow',
  INSPECTION_PENDING: 'purple',
  OUT_OF_SERVICE: 'red',
  RETIRED: 'gray',
} as const;

export const MAINTENANCE_STATUS_COLORS: Record<string, StatusColorPalette> = {
  SCHEDULED: 'blue',
  IN_PROGRESS: 'orange',
  COMPLETED: 'green',
  CANCELLED: 'gray',
  OVERDUE: 'red',
} as const;

export const DOCUMENT_STATUS_COLORS: Record<string, StatusColorPalette> = {
  APPROVED: 'green',
  REJECTED: 'red',
  PENDING_APPROVAL: 'yellow',
  DRAFT: 'gray',
  ARCHIVED: 'purple',
} as const;

export const TENANT_STATUS_COLORS: Record<string, StatusColorPalette> = {
  ACTIVE: 'green',
  TRIAL: 'yellow',
  SUSPENDED: 'red',
  CANCELLED: 'gray',
  DELETED: 'gray',
} as const;

export const DISTRIBUTION_HUB_STATUS_COLORS: Record<string, StatusColorPalette> = {
  ACTIVE: 'green',
  INACTIVE: 'gray',
  MAINTENANCE: 'orange',
  CAPACITY_FULL: 'red',
  SEASONAL: 'purple',
} as const;

// ── WMS Entity Status Colors ──

export const WMS_TASK_STATUS_COLORS: Record<string, StatusColorPalette> = {
  PENDING: 'yellow',
  ASSIGNED: 'blue',
  IN_PROGRESS: 'purple',
  COMPLETED: 'green',
  CANCELLED: 'red',
  ON_HOLD: 'orange',
  FAILED: 'red',
} as const;

export const WMS_TASK_PRIORITY_COLORS: Record<string, StatusColorPalette> = {
  LOW: 'gray',
  NORMAL: 'blue',
  HIGH: 'orange',
  URGENT: 'red',
  CRITICAL: 'red',
} as const;

export const WMS_RECEIVING_STATUS_COLORS: Record<string, StatusColorPalette> = {
  PENDING: 'yellow',
  RECEIVING: 'blue',
  PARTIALLY_RECEIVED: 'orange',
  COMPLETED: 'green',
  CANCELLED: 'red',
  CLOSED: 'gray',
} as const;

export const WMS_SHIPPING_STATUS_COLORS: Record<string, StatusColorPalette> = {
  PENDING: 'yellow',
  PICKING: 'blue',
  PACKING: 'purple',
  SHIPPED: 'green',
  CANCELLED: 'red',
  ON_HOLD: 'orange',
} as const;

export const WMS_LOCATION_STATUS_COLORS: Record<string, StatusColorPalette> = {
  ACTIVE: 'green',
  INACTIVE: 'gray',
  FULL: 'red',
  MAINTENANCE: 'orange',
  BLOCKED: 'red',
} as const;

export const WMS_PRODUCT_STATUS_COLORS: Record<string, StatusColorPalette> = {
  ACTIVE: 'green',
  INACTIVE: 'gray',
  DISCONTINUED: 'red',
} as const;

// ── Generic Status Colors (reusable for simple ACTIVE/INACTIVE entities) ──

export const GENERIC_ACTIVE_STATUS_COLORS: Record<string, StatusColorPalette> = {
  ACTIVE: 'green',
  INACTIVE: 'gray',
} as const;

/**
 * Lookup helper — returns color for a status string, defaulting to 'gray'.
 * Works with both uppercase and lowercase status values.
 */
export function getStatusColor(
  colorMap: Record<string, StatusColorPalette>,
  status: string | null | undefined
): StatusColorPalette {
  if (!status) return 'gray';
  return colorMap[status.toUpperCase()] ?? 'gray';
}
